import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, CircularProgress,
  LinearProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  doc, getDoc, collection, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import WorkshopPhotoCarousel from '../modules/workshop/components/WorkshopPhotoCarousel';

const accent = '#E05500';

const VERKTYG_FRÅGOR = [
  { id: 'v1', fråga: 'Hur upplevde du WorkshopN som verktyg under workshopen?', typ: 'skala' },
  { id: 'v2', fråga: 'Vad fungerade särskilt bra med det digitala upplägget?', typ: 'text' },
  { id: 'v3', fråga: 'Vad saknades eller kunde fungerat bättre?', typ: 'text' },
  { id: 'v4', fråga: 'Skulle du rekommendera detta upplägg för andra workshops?', typ: 'skala' },
  { id: 'v5', fråga: 'Fri kommentar', typ: 'text' },
];

function ScaleInput({ value, onChange }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <Box key={n} onClick={() => onChange(n)}
            sx={{
              flex: 1, minHeight: 56, borderRadius: 2, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '1.2rem', userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              border: '2px solid',
              borderColor: value === n ? accent : '#ddd',
              bgcolor: value === n ? accent : '#fff',
              color: value === n ? '#fff' : '#666',
              transition: 'all 0.1s',
              touchAction: 'manipulation',
            }}>
            {n}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
        <Typography variant="caption" color="text.disabled">Inte alls</Typography>
        <Typography variant="caption" color="text.disabled">Mycket bra</Typography>
      </Box>
    </Box>
  );
}

function YesNoInput({ value, onChange }) {
  const options = [
    { val: 'ja', label: 'Ja' },
    { val: 'delvis', label: 'Delvis' },
    { val: 'nej', label: 'Nej' },
    { val: 'vet_ej', label: 'Vet ej' },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
      {options.map(o => (
        <Box key={o.val} onClick={() => onChange(o.val)}
          sx={{
            py: 1.75, borderRadius: 2, cursor: 'pointer', fontWeight: 600,
            fontSize: '1rem', border: '2px solid', userSelect: 'none', textAlign: 'center',
            WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            borderColor: value === o.val ? accent : '#ddd',
            bgcolor: value === o.val ? accent : '#fff',
            color: value === o.val ? '#fff' : '#555',
            transition: 'all 0.1s',
          }}>
          {o.label}
        </Box>
      ))}
    </Box>
  );
}

function QuestionBlock({ nr, fråga, typ, value, onChange }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', bgcolor: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>{nr}</Typography>
        </Box>
        <Typography fontWeight={600} fontSize="1rem" color="#222" sx={{ lineHeight: 1.5 }}>{fråga}</Typography>
      </Box>
      <Box sx={{ pl: { xs: 0, sm: 4.5 } }}>
        {typ === 'skala' && <ScaleInput value={value} onChange={onChange} />}
        {typ === 'janej' && <YesNoInput value={value} onChange={onChange} />}
        {typ === 'text' && (
          <TextField
            fullWidth multiline minRows={3}
            placeholder="Skriv ditt svar här..."
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                '&.Mui-focused fieldset': { borderColor: accent },
              },
            }}
            inputProps={{ style: { fontSize: 16 } }}
          />
        )}
      </Box>
    </Box>
  );
}

export default function EnkätSida() {
  const { workshopId, enkatId: enkätId, deltagarId, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enkät, setEnkät] = useState(null);
  const [workshopPhotos, setWorkshopPhotos] = useState([]);
  const [deltagare, setDeltagare] = useState(null);
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);

  const [step, setStep] = useState(1); // 1=Del1, 2=Del2, 3=Tack
  const [svar1, setSvar1] = useState({});
  const [svar2, setSvar2] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    try {
      if (!auth.currentUser) await signInAnonymously(auth);
      const enkätRef = doc(db, 'workshops', workshopId, 'enkäter', enkätId);
      // Deltagarlistan (e-post, token) kan inte läsas härifrån. Den personliga
      // länken har ett eget dokument, lankar/{token}, med namn och svarsstatus.
      const [enkätSnap, wsSnap, länkSnap] = await Promise.all([
        getDoc(enkätRef),
        getDoc(doc(db, 'workshops', workshopId)),
        getDoc(doc(enkätRef, 'lankar', token)).catch(() => null),
      ]);
      if (!enkätSnap.exists()) { setError('Enkäten hittades inte.'); setLoading(false); return; }
      setEnkät(enkätSnap.data());
      if (wsSnap.exists()) setWorkshopPhotos(wsSnap.data().photos || []);

      const länk = länkSnap?.exists() ? länkSnap.data() : null;
      if (!länk || länk.deltagarId !== deltagarId) { setError('Ogiltig länk.'); setLoading(false); return; }
      setDeltagare({ id: deltagarId, namn: länk.namn });

      if (länk.harSvarat) { setAlreadyAnswered(true); }
    } catch (e) {
      setError('Något gick fel. Försök igen.');
    }
    setLoading(false);
  }

  const frågorInhåll = (enkät?.frågorInhåll || []).filter(f => f.inkludera !== false);

  function del1Complete() {
    return frågorInhåll.every(f => {
      if (f.typ === 'text') return true;
      return svar1[f.id] !== undefined && svar1[f.id] !== '';
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError('');
    try {
      const svar1Array = frågorInhåll.map(f => ({
        frågaId: f.id,
        fråga: f.fråga,
        svar: svar1[f.id] ?? '',
        typ: f.typ,
      }));

      const svar2Array = VERKTYG_FRÅGOR.map(f => ({
        frågaId: f.id,
        fråga: f.fråga,
        svar: svar2[f.id] ?? '',
        typ: f.typ,
      }));

      const enkätRef = doc(db, 'workshops', workshopId, 'enkäter', enkätId);

      // Token kontrolleras av reglerna mot lankar/{token}. harSvarat sätts av
      // Cloud Function onEnkatSvar när svaret har skapats.
      await addDoc(collection(enkätRef, 'svar'), {
        deltagarId,
        token,
        namn: deltagare.namn,
        svaradAt: serverTimestamp(),
        svar: svar1Array,
      });

      // Del 2 är anonym – ingen token eller deltagarId. Ett misslyckande här
      // ska inte hindra bekräftelsen, svaret på Del 1 är redan sparat.
      try {
        await addDoc(collection(enkätRef, 'appfeedback'), {
          svaradAt: serverTimestamp(),
          svar: svar2Array,
        });
      } catch (e) {
        console.error('appfeedback', e);
      }

      setStep(3);
    } catch (e) {
      console.error(e);
      setSubmitError('Svaren kunde inte skickas. Kontrollera uppkopplingen och försök igen.');
    }
    setSubmitting(false);
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9' }}>
        <CircularProgress sx={{ color: accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9', p: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 400, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: '#bbb', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Ogiltig länk</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  if (alreadyAnswered) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9', p: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 400, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#4ade80', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Redan besvarat</Typography>
          <Typography color="text.secondary">Du har redan svarat på den här enkäten. Tack!</Typography>
        </Paper>
      </Box>
    );
  }

  if (step === 3) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9', p: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 480, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: accent, mb: 2 }} />
          <Typography variant="h5" fontWeight={800} gutterBottom>Tack, {deltagare?.namn}!</Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Dina svar har skickats. Din återkoppling hjälper oss att förbättra
            både workshopens innehåll och hur vi faciliterar framtida workshops.
          </Typography>
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="caption" color="text.disabled">
              Workshop<span style={{ color: accent }}>N</span> · LagbasN
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  const progress = step === 1 ? 33 : 66;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 6 }}>
      {/* Hero image */}
      {enkät?.workshopImageUrl ? (
        <Box sx={{ position: 'relative', height: { xs: 180, sm: 240 }, overflow: 'hidden' }}>
          <Box component="img" src={enkät.workshopImageUrl} alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
            px: 3, pb: 3,
          }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#fff', letterSpacing: 1 }}>
              Workshop<span style={{ color: accent }}>N</span>
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#fff" sx={{ textAlign: 'center', mt: 0.5 }}>
              {enkät.workshopTitle}
            </Typography>
            {enkät.workshopDate && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                {new Date(enkät.workshopDate + 'T00:00:00').toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ bgcolor: '#1a1a1a', py: 2.5, px: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', color: '#fff' }}>
            Workshop<span style={{ color: accent }}>N</span>
          </Typography>
          {enkät?.workshopTitle && (
            <Typography variant="subtitle1" fontWeight={700} color="rgba(255,255,255,0.85)" sx={{ mt: 0.5 }}>
              {enkät.workshopTitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Photo carousel */}
      {workshopPhotos.length > 0 && (
        <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, pt: 2 }}>
          <WorkshopPhotoCarousel photos={workshopPhotos} height={220} autoPlay />
        </Box>
      )}

      {/* Progress bar */}
      <Box sx={{ bgcolor: '#1a1a1a', px: 3, pb: 1.5 }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: accent } }} />
        </Box>
      </Box>

      <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 1.5, sm: 2 }, pt: 3 }}>
        {/* Step 1: Workshop content */}
        {step === 1 && (
          <>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Din upplevelse av workshopen
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Hej {deltagare?.namn}! Vi vill gärna höra vad du tyckte om workshopen.
              Det tar bara några minuter.
            </Typography>

            {enkät?.sammanfattning && (
              <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: '#ddd', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                  Workshopens resultat – en påminnelse
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#444' }}>
                  {enkät.sammanfattning}
                </Typography>
              </Paper>
            )}

            {enkät?.nästaSteg?.aktiv && enkät.nästaSteg.text && (
              <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: '#1a1a1a', border: `1px solid ${accent}55` }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: accent, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                  Nästa steg
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {enkät.nästaSteg.text}
                </Typography>
                {enkät.nästaSteg.datum && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1 }}>
                    📅 {enkät.nästaSteg.datum}
                  </Typography>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              {frågorInhåll.map((f, i) => (
                <QuestionBlock key={f.id} nr={i + 1} fråga={f.fråga} typ={f.typ}
                  value={svar1[f.id]}
                  onChange={v => setSvar1(prev => ({ ...prev, [f.id]: v }))}
                />
              ))}
            </Paper>

            <Button fullWidth variant="contained" size="large"
              onClick={() => setStep(2)}
              disabled={!del1Complete()}
              sx={{
                bgcolor: accent, '&:hover': { bgcolor: '#C94D00' },
                fontWeight: 700, py: 2, borderRadius: 2, fontSize: '1.05rem',
                mb: 3,
                '&.Mui-disabled': { bgcolor: '#ddd', color: '#999' },
              }}>
              Nästa →
            </Button>
          </>
        )}

        {/* Step 2: Tool feedback */}
        {step === 2 && (
          <>
            <Box sx={{
              bgcolor: '#1a1a1a', borderRadius: 2, p: 2.5, mb: 3,
              border: `1px solid ${accent}44`,
            }}>
              <Typography variant="subtitle2" fontWeight={700} color="#fff" gutterBottom>
                Del 2 – Helt anonymt
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                Vi använder WorkshopN för att facilitera workshops – och vi förbättrar det
                kontinuerligt baserat på era erfarenheter. Helt anonymt. Vi lever som vi lär.
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight={800} gutterBottom>
              Om verktyget och processen
            </Typography>

            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              {VERKTYG_FRÅGOR.map((f, i) => (
                <QuestionBlock key={f.id} nr={i + 1} fråga={f.fråga} typ={f.typ}
                  value={svar2[f.id]}
                  onChange={v => setSvar2(prev => ({ ...prev, [f.id]: v }))}
                />
              ))}
            </Paper>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="outlined" size="large" onClick={() => setStep(1)}
                sx={{ borderColor: '#ddd', color: '#666', fontWeight: 600, py: 1.5, borderRadius: 2, flex: 0 }}>
                ← Tillbaka
              </Button>
              <Button fullWidth variant="contained" size="large"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, fontWeight: 700, py: 1.5, borderRadius: 2, fontSize: '1rem' }}>
                {submitting ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Skicka svar'}
              </Button>
            </Box>

            {submitError && (
              <Typography color="error" variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                {submitError}
              </Typography>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              Del 2 är helt anonym – inga svar kopplas till dig
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
